import {
  type DragEvent,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Minus, Plus, RotateCw } from '../../shared/icons';
import {
  defaultLayout,
  loadLayout,
  saveLayout,
  type WidgetLayoutItem,
} from './widgets/layout';
import {
  nextSize,
  rowSpanClass,
  SIZE_GRID,
  SIZE_LABEL,
  spanClass,
  type WidgetDef,
  widgets,
} from './widgets/registry';

/**
 * home 视图：组件面板。widget 网格（lg 三列 × 8rem 行），布局
 * （顺序 / 尺寸 / 增删）持久在 localStorage。「编辑」进入布局态
 * （对齐 iOS 桌面小组件的编辑体验）：整卡拖拽排列、点按卡片循环
 * 切换尺寸档位（小 / 中 / 大）、左上角 − 移除；头部可添加还没上板
 * 的 widget、一键恢复默认。编辑中卡片内容整体不响应指针，避免
 * 误触 widget 自身交互。
 */
const Launcher = () => {
  const [items, setItems] = useState<WidgetLayoutItem[]>(loadLayout);
  const [editing, setEditing] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  // 正在拖拽的条目下标（render 用）；dragIndexRef 供拖拽事件读取
  const [dragging, setDragging] = useState<number | null>(null);
  const dragIndexRef = useRef<number | null>(null);
  // 刚完成拖拽的标记：拖放结束后浏览器可能补发 click，用它挡住
  // 误触尺寸切换
  const justDraggedRef = useRef(false);

  // 布局随改随存（挂载首存等于归一化回写，无副作用）
  useEffect(() => {
    saveLayout(items);
  }, [items]);

  const move = useCallback((from: number, to: number) => {
    setItems((current) => {
      if (from === to || to < 0 || to >= current.length) return current;
      const next = [...current];
      const [taken] = next.splice(from, 1);
      next.splice(to, 0, taken);
      return next;
    });
  }, []);

  const remove = useCallback((index: number) => {
    setItems((current) => current.filter((_, i) => i !== index));
  }, []);

  const cycleSize = useCallback((index: number) => {
    setItems((current) =>
      current.map((it, i) =>
        i === index ? { ...it, size: nextSize(it.size) } : it,
      ),
    );
  }, []);

  const reset = useCallback(() => {
    setItems(defaultLayout());
    setPickerOpen(false);
  }, []);

  // HTML5 拖拽换位：dragover 到目标卡时实时搬移（预览即结果），
  // dragend 落定——落盘由 items 的 effect 统一处理
  const onDragStart = (index: number) => (e: DragEvent<HTMLElement>) => {
    dragIndexRef.current = index;
    setDragging(index);
    e.dataTransfer.effectAllowed = 'move';
    // Firefox 需要有数据才起拖
    e.dataTransfer.setData('text/plain', '');
  };
  const onDragOver = (index: number) => (e: DragEvent<HTMLElement>) => {
    const from = dragIndexRef.current;
    if (from === null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (from !== index) {
      move(from, index);
      dragIndexRef.current = index;
      setDragging(index);
    }
  };
  const onDragEnd = useCallback(() => {
    dragIndexRef.current = null;
    setDragging(null);
    justDraggedRef.current = true;
  }, []);

  const onCardClick = (index: number) => () => {
    if (!editing) return;
    if (justDraggedRef.current) {
      justDraggedRef.current = false;
      return;
    }
    cycleSize(index);
  };

  const addWidget = (def: WidgetDef) => {
    setItems((current) => [...current, { key: def.key, size: def.size }]);
    setPickerOpen(false);
  };

  const added = new Set(items.map((it) => it.key));
  const available = widgets.filter((w) => !added.has(w.key));

  return (
    <div className="h-full overflow-y-auto bg-neutral-50">
      <div className="mx-auto w-full max-w-5xl px-6 py-6">
        <header className="flex items-center gap-2 pb-4">
          <h1 className="text-[13px] font-medium leading-5 text-neutral-500">
            组件
          </h1>
          {editing && (
            <>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setPickerOpen((v) => !v)}
                  disabled={available.length === 0}
                  className="flex items-center gap-1 rounded-lg border border-neutral-200 bg-white px-2.5 py-1 text-xs text-neutral-600 transition-colors hover:border-neutral-300 disabled:opacity-40"
                >
                  <Plus className="size-3.5" />
                  添加
                </button>
                {pickerOpen && (
                  <>
                    {/* 全屏透明垫：点击面板外任意处收起 */}
                    <button
                      type="button"
                      aria-hidden
                      tabIndex={-1}
                      onClick={() => setPickerOpen(false)}
                      className="fixed inset-0 z-10 cursor-default"
                    />
                    <ul className="absolute left-0 top-9 z-20 w-64 overflow-hidden rounded-xl border border-neutral-200 bg-white py-1 shadow-lg shadow-neutral-900/5">
                      {available.map((def) => (
                        <li key={def.key}>
                          <button
                            type="button"
                            onClick={() => addWidget(def)}
                            className="flex w-full flex-col gap-0.5 px-3 py-2 text-left transition-colors hover:bg-neutral-50"
                          >
                            <span className="text-sm text-neutral-800">
                              {def.title}
                            </span>
                            <span className="text-xs text-neutral-400">
                              {def.description}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
              <button
                type="button"
                onClick={reset}
                className="rounded-lg border border-neutral-200 bg-white px-2.5 py-1 text-xs text-neutral-600 transition-colors hover:border-neutral-300"
              >
                <RotateCw className="mr-1 inline size-3 align-[-1px]" />
                恢复默认
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => {
              setEditing((v) => !v);
              setPickerOpen(false);
            }}
            className="ml-auto rounded-lg border border-neutral-200 bg-white px-2.5 py-1 text-xs text-neutral-600 transition-colors hover:border-neutral-300"
          >
            {editing ? '完成' : '编辑'}
          </button>
        </header>

        {editing && items.length > 0 && (
          <p className="-mt-2 pb-3 text-xs text-neutral-400">
            拖动排列 · 点按卡片切换大小 · − 移除
          </p>
        )}

        {items.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-neutral-200 text-sm text-neutral-400">
            <p>面板还是空的</p>
            <p className="text-xs">点右上角「编辑」添加组件</p>
          </div>
        ) : (
          <ul className="grid grid-flow-row-dense grid-cols-1 gap-4 [grid-auto-rows:8rem] md:grid-cols-2 lg:grid-cols-3">
            {items.map((item, index) => {
              const def = widgets.find((w) => w.key === item.key);
              if (def === undefined) return null; // 归一化后不会发生，防御一下
              const grid = SIZE_GRID[item.size];
              return (
                <li
                  key={item.key}
                  draggable={editing}
                  onDragStart={onDragStart(index)}
                  onDragOver={onDragOver(index)}
                  onDragEnd={onDragEnd}
                  onDrop={onDragEnd}
                  onClick={onCardClick(index)}
                  tabIndex={editing ? 0 : undefined}
                  onKeyDown={
                    editing
                      ? (e) => {
                          // 键盘等价操作：Enter / 空格切换尺寸；
                          // 徽章等内部控件自己处理按键，不冒泡到这
                          if (e.target !== e.currentTarget) return;
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            cycleSize(index);
                          }
                        }
                      : undefined
                  }
                  title={editing ? '点按切换大小，拖动排列' : undefined}
                  aria-label={
                    editing
                      ? `${def.title}组件，${SIZE_LABEL[item.size]}尺寸，点按切换大小，拖动排列`
                      : undefined
                  }
                  className={`${spanClass(grid.span)} ${rowSpanClass(grid.rows)} relative flex min-w-0 flex-col ${
                    editing ? 'cursor-move select-none' : ''
                  }`}
                >
                  {/* iOS 式移除徽章：编辑态挂在卡片左上角 */}
                  {editing && (
                    <button
                      type="button"
                      title={`移除 ${def.title}`}
                      aria-label={`移除 ${def.title}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(index);
                      }}
                      className="absolute -left-2 -top-2 z-10 flex size-6 items-center justify-center rounded-full bg-neutral-800 text-white transition-colors hover:bg-neutral-600"
                    >
                      <Minus className="size-3.5" />
                    </button>
                  )}
                  <div
                    className={`min-h-0 flex-1 ${
                      editing ? 'pointer-events-none select-none' : ''
                    } ${dragging === index ? 'opacity-60' : ''}`}
                  >
                    <Suspense
                      fallback={
                        <div className="h-full rounded-2xl bg-white ring-1 ring-neutral-200/70" />
                      }
                    >
                      <def.Component />
                    </Suspense>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default Launcher;
